import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { getWeekRange } from '@/utils/weekRange';
import { dashboardKeys } from './queryKeys';

interface WeeklyHoursData {
  totalMinutes: number;
  formattedHours: string;
  entryCount: number;
}

/**
 * Calculate total hours worked this week from approved/pending time entries.
 * Uses clock_in/clock_out from time_entries table.
 */
const calculateWeeklyHours = async (profileId: string): Promise<WeeklyHoursData> => {
  // Get start and end of current week (Sunday-Saturday, system convention)
  const { weekStart, weekEnd } = getWeekRange(new Date());

  // Ensure user context is set before making queries that involve RLS policies
  await supabase.rpc('ensure_user_context');

  // Fetch time entries for this week that have both clock_in and clock_out
  const { data: entries, error } = await supabase
    .from('time_entries')
    .select('clock_in, clock_out')
    .eq('profile_id', profileId)
    .gte('clock_in', weekStart)
    .lte('clock_in', weekEnd)
    .not('clock_out', 'is', null);

  if (error) {
    console.error('Error fetching weekly time entries:', error);
    throw error;
  }

  // Calculate total minutes worked
  let totalMinutes = 0;
  for (const entry of entries || []) {
    if (entry.clock_in && entry.clock_out) {
      const clockIn = new Date(entry.clock_in);
      const clockOut = new Date(entry.clock_out);
      const diffMs = clockOut.getTime() - clockIn.getTime();
      totalMinutes += Math.floor(diffMs / (1000 * 60));
    }
  }

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return {
    totalMinutes,
    formattedHours: `${hours}h ${minutes}m`,
    entryCount: entries?.length || 0,
  };
};

export const useWeeklyHours = (profileId: string | undefined) => {
  return useQuery({
    queryKey: [...dashboardKeys.all, 'weeklyHours', profileId] as const,
    queryFn: () => calculateWeeklyHours(profileId!),
    enabled: !!profileId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
};
